// The cold-open presentation (IMPLEMENTATION §2.7 "Mode FSM", §0.4 u00–u01, UX §4 0:00–0:10, GDD §12).
// PURE. The sim owns the cold open (a Gallery in practice mode, derived by input/modes.ts); the UI adds the
// scripted beats around it:
//   cold       Sarge says the tapToFire tip ("Tap the Gallery to fire!") with the finger on the Gallery; the
//              chip and the tab bar are hidden (their rows stay reserved: nothing jumps)
//   outOfAmmo  the 13th tap clicked empty: "Out of ammo!" for coldOpen.outOfAmmoMs
//   dig        "Let's make our own. Dig!" for coldOpen.digMs (the Lead Vein has risen at (3,4)); the chip
//              slides in with it (§2.7: "after the tin is empty and the 'Dig!' line has shown")
//   play       tips follow the sim's tutorialView (the tab bar waits for the first sale, u04: §2.7)
// The input router is already in PLAY from `empty` (the sim ends the practice there, input/modes.ts); the
// two beats are display-only (proposed lead decision, M7 #2) — the shell only swallows Gallery-card taps
// during "Out of ammo!" (the player's rapid extra taps on the emptied Gallery).
// A game that starts outside the cold open (a save) is in `play` at once; a reset back to a cold open
// returns to `cold`. The two lines play only when the player's own click emptied the tin (`empty`, the
// 13th tap); a cold open ended any other way (a debug goal jump, a loaded save) goes straight to `play`.
// `replay` (Settings → Replay intro) plays the two lines again without touching the sim (the cold open itself
// needs a fresh game: Reset save).

export type IntroPhase = 'cold' | 'outOfAmmo' | 'dig' | 'play';

export interface IntroState {
  phase: IntroPhase;
  /** Wall ms the phase started. */
  since: number;
}

export interface IntroCfg {
  outOfAmmoMs: number;
  digMs: number;
}

export function startIntro(simCold: boolean, now: number): IntroState {
  return { phase: simCold ? 'cold' : 'play', since: now };
}

/**
 * Advances the phase for the sim's cold-open flag at wall time `now`; `clicked` = the practice tin was
 * emptied by the player's own tap (an `empty` event) since the cold open started.
 */
export function stepIntro(
  st: IntroState,
  simCold: boolean,
  now: number,
  cfg: IntroCfg,
  clicked = true,
): IntroState {
  if (simCold) return st.phase === 'cold' ? st : { phase: 'cold', since: now };
  switch (st.phase) {
    case 'cold':
      return clicked ? { phase: 'outOfAmmo', since: now } : { phase: 'play', since: now };
    case 'outOfAmmo':
      return now - st.since >= cfg.outOfAmmoMs
        ? { phase: 'dig', since: st.since + cfg.outOfAmmoMs }
        : st;
    case 'dig':
      return now - st.since >= cfg.digMs ? { phase: 'play', since: st.since + cfg.digMs } : st;
    case 'play':
      return st;
  }
}

/** Replays the scripted lines (Settings → Replay intro); only outside the cold open. */
export function replayIntro(st: IntroState, now: number): IntroState {
  return st.phase === 'cold' ? st : { phase: 'outOfAmmo', since: now };
}

/** copy.json key of Sarge's scripted line in this phase (null: the tip, if any, speaks). */
export function introLineKey(phase: IntroPhase): string | null {
  if (phase === 'outOfAmmo') return 'sarge.outOfAmmo';
  if (phase === 'dig') return 'sarge.dig';
  return null;
}

/** The goal chip shows once the "Dig!" line has shown (§2.7), not during the cold open or "Out of ammo!". */
export function chipShown(phase: IntroPhase): boolean {
  return phase === 'dig' || phase === 'play';
}
